import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { asObjectId } from '../../utils/references';
import { useTenantContractTerms, useTenantPaymentTerms } from '../../utils/productCatalog';
import { seedPgm } from './reviseSeed';
import { defaultEffectiveDate } from './offerDefaults';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

let rowIdCounter = 0;
const newRowId = () => `emp_${++rowIdCounter}_${Date.now()}`;

const DEFAULT_POLICYHOLDER = {
  companyName: '',
  address: '',
  canton: '',
  nogaCode: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

const emptyEmployee = () => ({
  id: newRowId(),
  nom: '',
  prenom: '',
  dateOfBirth: '',
  gender: '',
  annualSalary: '',
});

// Bulk paste → roster rows. Returns { rows, errors }.
//
// Accepts every shape between a full roster and a bare payroll line, because
// date of birth and sex are not PGM rating inputs and the engine no longer
// demands them (see backend services/pgmPricingService.js):
//   5 cols: Nom \t Prénom \t YYYY-MM-DD \t M|F|H \t salary
//   4 cols: FullName \t YYYY-MM-DD \t M|F|H \t salary  (full name lands in `nom`)
//   3 cols: FullName \t YYYY-MM-DD \t salary   OR   FullName \t M|F|H \t salary
//   2 cols: FullName \t salary
// Tolerates tab, comma, or 2+ spaces as delimiters.
// "H" (Homme) is accepted as a synonym for "M".
//
// Columns are consumed from the RIGHT and identified by shape, not position,
// so a missing field shifts nothing. What has NOT been loosened: a value that
// is clearly meant to be a date still has to be a valid YYYY-MM-DD, so a
// typo'd birthdate is still an error instead of being swallowed into the name.
const GENDER_TOKEN = /^[MFH]$/i;
const DATE_TOKEN = /\d{2,4}[-./]\d{1,2}[-./]\d{1,4}/;

export function parseBulkPaste(text) {
  const rows = [];
  const errors = [];
  const lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  lines.forEach((line, i) => {
    const cols = line.split(/\t|\s{2,}|,/).map(c => c.trim()).filter(Boolean);
    if (cols.length < 2) {
      errors.push({ line: i + 1, message: `Expected 2 to 5 columns (Nom [Prénom] [DOB] [Sexe] Salaire), got ${cols.length}` });
      return;
    }

    const rest = cols.slice(0, cols.length - 1);
    const salaryStr = cols[cols.length - 1].replace(/['\s]/g, '');

    const salary = Number(salaryStr);
    if (!isFinite(salary) || salary <= 0) {
      errors.push({ line: i + 1, message: `Salaire invalide: "${salaryStr}"` });
      return;
    }

    let gender = '';
    if (rest.length > 1 && GENDER_TOKEN.test(rest[rest.length - 1])) {
      gender = rest.pop().toUpperCase();
      if (gender === 'H') gender = 'M';
    }

    let dob = '';
    if (rest.length > 1 && DATE_TOKEN.test(rest[rest.length - 1])) {
      dob = rest.pop();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dob) || isNaN(new Date(dob).getTime())) {
        errors.push({ line: i + 1, message: `Date de naissance invalide: "${dob}" (format YYYY-MM-DD)` });
        return;
      }
    }

    const nameTokens = rest;
    if (nameTokens.length === 0) {
      errors.push({ line: i + 1, message: 'Nom requis' });
      return;
    }
    // A leftover date among the name tokens means the row was meant to carry
    // a birthdate and something after it did not parse — report it rather than
    // filing the broken value away inside somebody's surname. Whatever sits
    // BETWEEN that date and the salary was meant to be the sex, so name it.
    const strayAt = nameTokens.findIndex(t => DATE_TOKEN.test(t));
    if (strayAt >= 0) {
      const trailing = nameTokens.slice(strayAt + 1);
      errors.push(trailing.length === 1
        ? { line: i + 1, message: `Sexe doit être H/M ou F, reçu "${trailing[0]}"` }
        : { line: i + 1, message: `Date de naissance invalide: "${nameTokens[strayAt]}" (format YYYY-MM-DD)` });
      return;
    }

    let nom, prenom;
    if (nameTokens.length === 2) {
      nom = nameTokens[0];
      prenom = nameTokens[1];
    } else {
      nom = nameTokens.join(' ');
      prenom = '';
    }

    rows.push({ id: newRowId(), nom, prenom, dateOfBirth: dob, gender, annualSalary: String(salary) });
  });
  return { rows, errors };
}

function composeName(prenom, nom) {
  return [prenom, nom].map(s => (s || '').trim()).filter(Boolean).join(' ');
}

const usePgmOfferFunctions = ({ onCreated, onClose, reviseFrom = null, editDraft = null }) => {
  // Revise mode: seed every field — including the employee roster — from
  // the offer being revised so nothing has to be re-entered.
  // Seeded from the offer being REVISED (a new version of an issued
  // proposal) or the DRAFT being edited in place. Same reconstruction
  // either way — the difference is only what the save call does, so the
  // two flows cannot drift in what they carry over.
  const seedSource = reviseFrom || editDraft;
  const seed = seedSource ? seedPgm(seedSource) : null;
  // Contract durations the tenant offers, in years. Unset = the platform
  // default [1, 2, 3], which is exactly the list this form hardcoded
  // before the field existed.
  const contractTerms = useTenantContractTerms();
  // Which frequencies this tenant may quote PGM at, and which is default.
  // Previously a hardcoded three-item list in the form; the backend now
  // rejects anything outside frequenciesAllowed, so this only narrows the
  // dropdown to match what the API will actually accept.
  const paymentTerms = useTenantPaymentTerms('PGM');
  const [rateTable, setRateTable]               = useState(null);
  const [rateTableLoading, setRateTableLoading] = useState(true);
  const [rateTableError, setRateTableError]     = useState('');

  const [policyholder, setPolicyholder] = useState(() => seed?.policyholder || DEFAULT_POLICYHOLDER);
  const [clientId, setClientId] = useState(() => seed?.clientId || '');
  const [brokerId, setBrokerId] = useState(() => seed?.brokerId || '');
  const [productConfig, setProductConfig] = useState(() => ({
    waitingPeriod: '',
    coveragePercent: '',
    effectiveDate: defaultEffectiveDate(),
    paymentFrequency: '',
    contractTermYears: null,
    ...(seed?.productConfig || {}),
  }));
  const [employees, setEmployees] = useState(() => seed?.employees || [emptyEmployee()]);

  const [calc, setCalc]                 = useState(null);
  const [calcLoading, setCalcLoading]   = useState(false);
  const [calcError, setCalcError]       = useState('');

  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');

  const debounceRef = useRef(null);
  // Tracks policyholder fields the last selectClient() populated. Keeps them
  // eligible for overwrite on the next selection, without touching anything
  // the user has since typed manually.
  const autoFilledRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/offers/pgm/rate-table`, { withCredentials: true });
        if (cancelled) return;
        const data = resp.data?.data;
        setRateTable(data);
        setProductConfig(prev => ({
          ...prev,
          waitingPeriod:   prev.waitingPeriod   || String(data?.availableWaitingPeriods?.[0]   ?? ''),
          coveragePercent: prev.coveragePercent || String(data?.availableCoveragePercents?.[0] ?? ''),
        }));
        setPolicyholder(prev => ({
          ...prev,
          nogaCode: prev.nogaCode || data?.constants?.defaultNogaCode || '862200',
        }));
      } catch (err) {
        if (!cancelled) setRateTableError(err.response?.data?.message || 'Failed to load PGM rate table');
      } finally {
        if (!cancelled) setRateTableLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const salaryCap = rateTable?.constants?.salaryCap ?? 300000;

  const selectedRateRow = useMemo(() => {
    if (!rateTable || !productConfig.waitingPeriod || !productConfig.coveragePercent) return null;
    return (rateTable.rates || []).find(r =>
      Number(r.waitingPeriod)   === Number(productConfig.waitingPeriod) &&
      Number(r.coveragePercent) === Number(productConfig.coveragePercent)
    ) || null;
  }, [rateTable, productConfig.waitingPeriod, productConfig.coveragePercent]);

  const updatePolicyholder = useCallback((field, value) => {
    // A manual edit drops the field from the auto-filled set so a later
    // client reselection cannot overwrite the user's typing.
    autoFilledRef.current.delete(field);
    setPolicyholder(prev => ({ ...prev, [field]: value }));
  }, []);

  // Prefill policyholder fields from the selected Client. Writes into any
  // field that is empty OR was previously auto-filled by an earlier client
  // selection; never touches values the user has typed themselves.
  const selectClient = useCallback((client) => {
    if (!client) { setClientId(''); return; }
    setClientId(client._id);
    setPolicyholder(prev => {
      const next = { ...prev };
      const newlyFilled = new Set();
      const trySet = (field, value) => {
        const wasAuto  = autoFilledRef.current.has(field);
        const canWrite = !prev[field] || wasAuto;
        if (!canWrite) return;             // user typed here — leave alone
        if (!value) {
          if (wasAuto) next[field] = '';   // clear stale prior-client data
          return;
        }
        next[field] = value;
        newlyFilled.add(field);
      };
      trySet('companyName', client.companyName || client.displayName || '');
      const addr = client.address || {};
      const addrLine = [addr.street, addr.postalCode, addr.city, addr.country]
        .map(s => (s || '').trim()).filter(Boolean).join(', ');
      trySet('address', addrLine);
      const contactName = client.contactPerson
        || [client.firstName, client.lastName].filter(Boolean).join(' ');
      trySet('contactName',  contactName);
      trySet('contactEmail', client.email);
      trySet('contactPhone', client.phone);
      autoFilledRef.current = newlyFilled;
      return next;
    });
  }, []);
  const clearClient = useCallback(() => setClientId(''), []);
  const updateProductConfig = useCallback((field, value) =>
    setProductConfig(prev => ({ ...prev, [field]: value })),
  []);
  const updateEmployee = useCallback((id, field, value) => {
    setEmployees(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  }, []);
  const addEmployee = useCallback(() => {
    setEmployees(prev => [...prev, emptyEmployee()]);
  }, []);
  const removeEmployee = useCallback((id) => {
    setEmployees(prev => prev.length <= 1 ? prev : prev.filter(e => e.id !== id));
  }, []);
  const replaceRoster = useCallback((rows) => {
    setEmployees(rows.length > 0 ? rows : [emptyEmployee()]);
  }, []);

  const employeeRows = useMemo(() => employees.map(e => {
    const annual = Number(e.annualSalary);
    const capped = isFinite(annual) && annual > 0 ? Math.min(annual, salaryCap) : 0;
    return { ...e, _capped: capped, _wasCapped: isFinite(annual) && annual > salaryCap };
  }), [employees, salaryCap]);
  // On a company policy the cap is PER EMPLOYEE, and it caps the rating
  // basis — not the roster. A real payroll contains people above the cap,
  // so these rows are named for disclosure (the broker should know whose
  // salary is only partly insured) but never block submission: the entered
  // salary is kept and the premium rates on `_capped`.
  const cappedEmployeeNames = useMemo(
    () => employeeRows.filter(e => e._wasCapped)
      .map((e, i) => composeName(e.prenom, e.nom) || `#${i + 1}`),
    [employeeRows],
  );
  const salaryCapped = cappedEmployeeNames.length > 0;
  const totalPayrollPreview = useMemo(
    () => employeeRows.reduce((sum, e) => sum + e._capped, 0),
    [employeeRows],
  );

  const buildCalcPayload = useCallback(() => ({
    employees: employees.map(e => ({
      name: composeName(e.prenom, e.nom),
      nom: e.nom,
      prenom: e.prenom,
      dateOfBirth: e.dateOfBirth,
      gender: e.gender,
      annualSalary: Number(e.annualSalary),
    })),
    waitingPeriod: Number(productConfig.waitingPeriod),
    coveragePercent: Number(productConfig.coveragePercent),
    effectiveDate: productConfig.effectiveDate,
  }), [employees, productConfig.waitingPeriod, productConfig.coveragePercent, productConfig.effectiveDate]);

  const canCalculate = useMemo(() => {
    if (!productConfig.waitingPeriod || !productConfig.coveragePercent) return false;
    for (const e of employees) {
      if (!e.nom?.trim()) return false;
      // Date of birth and gender are not rating inputs for PGM and are not
      // required by the engine, so the form must not gate on them either —
      // a roster that is a payroll total and nothing else still quotes.
      // A gender that IS entered must still be M or F, matching the server.
      if (e.gender && e.gender !== 'M' && e.gender !== 'F') return false;
      const salary = Number(e.annualSalary);
      if (!isFinite(salary) || salary <= 0) return false;
    }
    return true;
  }, [productConfig.waitingPeriod, productConfig.coveragePercent, employees]);

  const runCalculate = useCallback(async () => {
    if (!canCalculate) {
      setCalc(null);
      setCalcError('');
      return;
    }
    setCalcLoading(true);
    setCalcError('');
    try {
      const resp = await axios.post(
        `${BASE_URL}/offers/pgm/calculate`,
        buildCalcPayload(),
        { withCredentials: true },
      );
      setCalc(resp.data?.data);
    } catch (err) {
      setCalcError(err.response?.data?.message || 'Could not calculate premium');
      setCalc(null);
    } finally {
      setCalcLoading(false);
    }
  }, [canCalculate, buildCalcPayload]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runCalculate, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [runCalculate]);

  const submit = useCallback(async (status = 'sent') => {
    setSaving(true);
    setSaveError('');
    try {
      const resp = await axios.post(
        `${BASE_URL}/offers/pgm`,
        {
          policyholder,
          clientId: asObjectId(clientId) || undefined,
          // Only a real id is ever posted as a reference — never a display value.
          ...(asObjectId(brokerId) && { broker: asObjectId(brokerId) }),
          productConfig: {
            ...buildCalcPayload(),
            // Empty means "the user did not override the default"; send
            // the tenant's configured default rather than a blank the
            // backend would have to guess at.
            paymentFrequency: productConfig.paymentFrequency || paymentTerms.frequency,
            contractTermYears: productConfig.contractTermYears ?? contractTerms[0],
          },
          status,
          ...(reviseFrom?._id && { revisionOf: reviseFrom._id }),
          // Editing a draft returns to the SAME product endpoint with the
          // draft's id, so the premium and the params snapshot are rebuilt
          // by the identical code that built them the first time. The
          // server updates in place: same _id, same proposal number.
          ...(editDraft?._id && { draftId: editDraft._id }),
        },
        { withCredentials: true },
      );
      onCreated && onCreated(resp.data?.data);
      onClose && onClose();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to create offer');
    } finally {
      setSaving(false);
    }
  }, [policyholder, clientId, brokerId, productConfig.paymentFrequency, productConfig.contractTermYears, contractTerms, paymentTerms.frequency, reviseFrom, editDraft, buildCalcPayload, onCreated, onClose]);

  return {
    rateTable, rateTableLoading, rateTableError,
    salaryCap, salaryCapped, cappedEmployeeNames,
    contractTerms,
    paymentTerms,
    selectedRateRow,
    policyholder, updatePolicyholder,
    clientId, selectClient, clearClient,
    brokerId, setBrokerId,
    productConfig, updateProductConfig,
    employees: employeeRows, totalPayrollPreview,
    updateEmployee, addEmployee, removeEmployee, replaceRoster,
    calc, calcLoading, calcError,
    canCalculate,
    saving, saveError,
    saveDraft: () => submit('draft'),
    createOffer: () => submit('sent'),
  };
};

export default usePgmOfferFunctions;
